import React, { useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { IconChevronUp, IconChevronDown, IconSuccess } from './icons/IconPack';

interface JobListingProps {
  title: string;
  location: string;
  type: string;
  description: string;
  requirements: string[];
  preferred?: string[];
  delay: number;
  language: string;
}

const JobListing: React.FC<JobListingProps> = ({ title, location, type, description, requirements, preferred, delay, language }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  
  const [isExpanded, setIsExpanded] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch('http://localhost:3001/api/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          title,
          location,
          type
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit application');
      }
      
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit application. Please try again later.');
      console.error('Error submitting application:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div 
      ref={ref}
      className={`card-professional transition-standard ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div 
        className="p-6 cursor-pointer" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <h3 className="text-heading-md text-blue-800">{title}</h3>
          <button 
            className="icon-container w-10 h-10"
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? <IconChevronUp /> : <IconChevronDown />}
          </button>
        </div>
        <div className="flex gap-4 mt-2">
          <span className="text-body-sm text-gray-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
            </svg>
            {location}
          </span>
          <span className="text-body-sm text-gray-600 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {type}
          </span>
        </div>
      </div>
      
      {isExpanded && (
        <div className="px-6 pb-6">
          <div className="pt-4 border-t border-gray-100">
            <p className="text-body-md text-gray-700 mb-6">{description}</p>
            
            <h4 className="text-heading-sm text-blue-700 mb-3">{language === 'zh' ? '工作要求' : 'Requirements'}</h4>
            <ul className="list-disc pl-5 mb-8 space-y-2">
              {requirements.map((req, index) => (
                <li key={index} className="text-body-sm text-gray-700">{req}</li>
              ))}
            </ul>
            
            {preferred && preferred.length > 0 && (
              <>
                <h4 className="text-heading-sm text-blue-700 mb-3">{language === 'zh' ? '加分條件' : 'Preferred'}</h4>
                <ul className="list-disc pl-5 mb-8 space-y-2">
                  {preferred.map((pref, index) => (
                    <li key={index} className="text-body-sm text-gray-700">{pref}</li>
                  ))}
                </ul>
              </>
            )}
            
            {!submitted ? (
              <div className="pt-6 border-t border-gray-100">
                <h4 className="text-heading-sm text-blue-700 mb-2">{language === 'zh' ? '對這個職位感興趣？' : 'Interested in this position?'}</h4>
                <p className="text-body-sm text-gray-600 mb-4">{language === 'zh' ? '留下您的電子郵件，我們將與您聯繫，提供更多關於這個職位的資訊。' : 'Leave your email and we\'ll contact you with more information about this role.'}</p>
                
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
                    {error}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input 
                    type="email" 
                    placeholder={language === 'zh' ? '您的電子郵件地址' : 'Your email address'}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="flex-1 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                  <button 
                    type="submit"
                    className="button-primary sm:w-auto"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (language === 'zh' ? '提交中...' : 'Submitting...') : (language === 'zh' ? '立即申請' : 'Apply Now')}
                  </button>
                </form>
              </div>
            ) : (
              <div className="pt-6 border-t border-gray-100 flex items-center text-green-700">
                <div className="icon-container mr-3">
                  <IconSuccess className="w-5 h-5" />
                </div>
                <p className="text-body-md">{language === 'zh' ? '感謝您的關注！我們將盡快與您聯繫，提供更多詳情。' : 'Thank you for your interest! We\'ll contact you with more details soon.'}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface CareersProps {
  language: string;
}

const Careers: React.FC<CareersProps> = ({ language }) => {
  const { ref: titleRef, inView: titleVisible } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const jobListings = [
    {
      title: language === 'zh' ? "AI 工程師" : "AI Engineer",
      location: language === 'zh' ? "台中，台灣" : "Taichung, Taiwan",
      type: language === 'zh' ? "全職" : "Full-time",
      description: language === 'zh'
        ? "加入我們的團隊，成為一名 AI 工程師，參與我們創新的 AI 開發團隊。我們提供系統化的 AI 系統開發培訓，為有志於成為 AI 專業人士的人提供一個展示才華的平台。無需經驗 - 我們將幫助您成長並在人工智能領域中脫穎而出。"
        : "Join our team as an AI Engineer and be part of our innovative AI development team. We offer systematic training in AI system development, providing a platform for aspiring AI professionals to shine. No prior experience needed - we'll help you grow and excel in the field of artificial intelligence.",
      requirements: language === 'zh' ? [
        "將 AI 應用於實際解決方案",
        "負責數據收集、模型構建、優化和部署",
        "分析、設計和編寫 AI 軟體程式",
        "須提交過去的 AI 分析範例或作品集",
        "開發基於大型語言模型（LLM）的 AI 應用",
        "具備 OpenAI API 或類似 API 的經驗",
        "擅長微調開源大型語言模型（LLM）",
        "熟悉機器學習和深度學習",
        "精通 Python",
        "具備 PyTorch 或 TensorFlow 經驗",
        "熟悉 AI 訓練數據收集"
      ] : [
        "Apply AI to practical solutions",
        "Manage data collection, model building, optimization, and deployment",
        "Analyze, design, and program AI software",
        "Submit past AI analysis examples or portfolios",
        "Develop LLM-based AI applications",
        "Experience with OpenAI API or similar",
        "Skilled in fine-tuning open-source LLMs",
        "Knowledge of machine learning and deep learning",
        "Proficient in Python",
        "Experience with PyTorch or TensorFlow",
        "Familiar with AI training data collection"
      ],
      delay: 100
    },
    {
      title: language === 'zh' ? "前端工程師" : "Frontend Engineer",
      location: language === 'zh' ? "台中，台灣" : "Taichung, Taiwan",
      type: language === 'zh' ? "全職" : "Full-time",
      description: language === 'zh'
        ? "加入我們的團隊，為網頁和移動應用創建創新且用戶友好的界面。您將與 AI 工程師和 UX 設計師密切合作，構建響應式、直觀的界面，使終端用戶能夠輕鬆使用先進技術。"
        : "Join our team to create innovative and user-friendly interfaces for both web and mobile applications. You'll work closely with AI engineers and UX designers to build responsive, intuitive interfaces that make advanced technology accessible to end-users across all platforms.",
      requirements: language === 'zh' ? [
        "在生產環境中具有豐富的 React 或 Vue.js 經驗",
        "具備 Flutter 框架的 iOS 和 Android 開發經驗",
        "精通現代 JavaScript 和 TypeScript",
        "理解響應式設計原則和無障礙標準",
        "具備狀態管理解決方案經驗（Redux、Vuex、Provider、Bloc）",
        "熟悉現代 CSS 框架和預處理器",
        "理解網頁和移動端性能優化",
        "具備 RESTful API 和 GraphQL 經驗",
        "具備測試框架經驗（Jest、React Testing Library）",
        "了解原生 iOS 和 Android 開發",
        "具備應用商店提交流程經驗",
        "作品集展示跨平台 UI/UX 實現能力"
      ] : [
        "Strong experience with React or Vue.js in production environments",
        "Experience with Flutter framework for iOS and Android development",
        "Advanced knowledge of modern JavaScript and TypeScript",
        "Understanding of responsive design principles and accessibility standards",
        "Experience with state management solutions (Redux, Vuex, Provider, Bloc)",
        "Knowledge of modern CSS frameworks and preprocessors",
        "Understanding of web and mobile performance optimization",
        "Experience with RESTful APIs and GraphQL",
        "Experience with testing frameworks (Jest, React Testing Library)",
        "Knowledge of native iOS and Android development",
        "Experience with app store submission processes",
        "Portfolio demonstrating UI/UX implementation capabilities across platforms"
      ],
      delay: 200
    },
    {
      title: language === 'zh' ? "AI 系統工程師" : "AI System Engineer",
      location: language === 'zh' ? "台中，台灣" : "Taichung, Taiwan",
      type: language === 'zh' ? "全職" : "Full-time",
      description: language === 'zh'
        ? "加入迪衡數位科技的 AI 團隊，設計、構建和運營端到端 AI 平台——從數據管道和模型訓練到生產部署和監控——與台灣頂尖的本地 AI 專業人士一起工作。"
        : "Join DHT Solution's AI team to design, build, and operate end-to-end AI platforms—from data pipelines and model training to production deployment and monitoring—working alongside Taiwan's top local AI professionals.",
      requirements: language === 'zh' ? [
        "精通 Python（或類似語言）和 DevOps 基礎知識",
        "具備 Linux 服務器、網絡、虛擬化（本地/雲端）經驗",
        "使用 Docker 容器化；在 Kubernetes 上編排",
        "構建 AI-ETL 管道，自動化 ML 工作流程（MLOps）",
        "開發和維護表格和圖像預測系統",
        "管理模型生命週期：訓練、驗證、版本控制、監控",
        "前端站點設置和微服務集成",
        "解決跨系統問題；記錄設計和概念驗證",
        "AI/ML 工作作品集或示例"
      ] : [
        "Strong coding in Python (or similar) and DevOps fundamentals",
        "Experience with Linux servers, networking, virtualization (on-prem/cloud)",
        "Containerization with Docker; orchestration on Kubernetes",
        "Build AI-ETL pipelines, automate ML workflows (MLOps)",
        "Develop and maintain tabular & image prediction systems",
        "Manage model lifecycle: training, validation, versioning, monitoring",
        "Front-end site setup and microservices integration",
        "Troubleshoot cross-system issues; document designs and PoCs",
        "Portfolio or examples of past AI/ML work"
      ],
      preferred: language === 'zh' ? [
        "雲端（Azure/AWS）經驗",
        "PostgreSQL、MongoDB、Kafka/Redis、NATS",
        "熟悉 MLflow、Kubeflow、HPC AI 基礎設施",
        "敏捷開發、強大的溝通能力和團隊精神"
      ] : [
        "Cloud (Azure/AWS) experience",
        "PostgreSQL, MongoDB, Kafka/Redis, NATS",
        "Familiarity with MLflow, Kubeflow, HPC AI infrastructure",
        "Agile development, strong communication, and team mindset"
      ],
      delay: 300
    }
  ];

  return (
    <section id="careers" className="py-24 px-4 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        <div 
          ref={titleRef}
          className={`text-center mb-16 ${
            titleVisible ? 'animate-fade-in' : 'opacity-0'
          }`}
        >
          <h2 className="text-heading-lg mb-4 section-heading-underline centered">
            {language === 'zh' ? '工作機會' : 'Career Opportunities'}
          </h2>
          <div className="text-body-lg text-gray-700 max-w-3xl mx-auto space-y-6">
            <p className="font-semibold">
              {language === 'zh' ? '加入台灣頂尖的 AI 創新者行列' : 'Join Taiwan\'s Leading AI Innovators'}
            </p>
            <p>
              {language === 'zh' 
                ? '在迪衡數位科技（DHT Solution），我們致力於突破人工智能的界限，並誠邀您加入我們的團隊。作為台灣頂尖AI團隊的一員，您將與最優秀的本地 AI 專業人士每日合作，共享對尖端技術的熱情與活力。'
                : 'At DHT Solution, we\'re on a mission to push the boundaries of artificial intelligence—and we want you on our team. As part of our Taipei-based AI powerhouse, you\'ll collaborate daily with the best local AI professionals, fueled by a shared passion and energy for cutting-edge development.'}
            </p>
            <ul className="list-disc list-inside ml-4 space-y-2 text-left">
              <li>
                <b>{language === 'zh' ? '現代化設施：' : 'Modern Facilities:'}</b>
                {language === 'zh' 
                  ? '在先進實驗室中進行原型設計、模型訓練和實驗。'
                  : ' Work in advanced labs for prototyping, model training, and experimentation.'}
              </li>
              <li>
                <b>{language === 'zh' ? '敏捷軟體開發流程：' : 'Agile Workflow:'}</b>
                {language === 'zh'
                  ? '採用精簡的敏捷流程，保持以客戶為中心並領先趨勢。'
                  : ' Use a streamlined Agile process to stay customer-focused and ahead of trends.'}
              </li>
              <li>
                <b>{language === 'zh' ? '支持性文化：' : 'Supportive Culture:'}</b>
                {language === 'zh'
                  ? '在靈活、以結果為導向的環境中蓬勃發展，享有工作與生活平衡及開放溝通。'
                  : ' Thrive in a flexible, results-driven environment with work–life balance and open communication.'}
              </li>
            </ul>
            <p>
              {language === 'zh'
                ? '如果您熱衷於用 AI 解決商業挑戰，並喜歡台灣新創的動態氛圍，立即申請，與我們一起塑造未來！'
                : 'If you love solving business challenges with AI and enjoy a dynamic Taiwanese startup vibe, apply today to shape the future with us!'}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-8">
          {jobListings.map((job, index) => (
            <JobListing 
              key={index}
              title={job.title}
              location={job.location}
              type={job.type}
              description={job.description}
              requirements={job.requirements}
              preferred={job.preferred}
              delay={job.delay}
              language={language}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Careers; 